import React, { useEffect, useRef, useState } from "react";
import LoanProductsModal from "../Modals/LoanProductsModal";
import TPOProductsModal from "../Modals/TPOProductsModal";
import Offers from "../offers";
import { useOffers } from "../../hooks/contexts/offers-hook";

import { getLightReachTpoOffers } from "../../dtos/lightreach";
import { getGoodLeapTpoOffers } from "../../dtos/goodleap";
import { CircularProgress } from "@material-ui/core";

/**
 * This function/component exists to create and launch the popup modal for listing loans and TPOs
 *
 * @callback setPpwCallback
 * @callback setLoanAIdCallBack
 *
 * @param {Array} mappedPrices Mapped PPW prices
 * @param {setPpwCallback} setPpw Callback to update current PPW value
 * @param {Array} loanList List of Loans available for the current customer
 * @param {CustomerData} customerData Object containing details on customer data
 * @param {setLoanAIdCallBack} setLoanAId Callback to update current loan based on id (mostly)
 * @param {string} optionType Loan option type (Loan | Cash | TPO)
 * @param {number} ppw Current select PPW value
 * @param {Object} Loan Current selected Loan
 * @param {string} provider Current loan provider (e.g. GoodLeap, Sungage, etc.)
 * @param {number} estimatedAnnualProduction Customers estimated annual power production
 * @param {number} systemSize Power size of customers system
 * @param {number} batteryCost Cost of batteries else 0
 * @param {string} sfProjectId Sales Force ProjectId
 * @param {string} lightReachAcctId Customers LightReach Account Id
 * @returns
 */

function ProductsPopup({
  setNeedToResyncTpoPricing,
  mappedPrices,
  setPpw,
  loanList,
  customerData,
  mspForLightReachGetQuote,
  setLoanAId,
  optionType,
  ppw,
  adderTotal,
  loan,
  provider,
  payload,
  estimatedAnnualProduction,
  systemSize,
  batteryCost,
  acCoupled,
  acCoupledAmount,
  sfProjectId,
  lightReachAcctId,
  setLightReachAcctId,
  dealerFee,
  fetchSungagePayments,
  setDealerFee,
  expansionPack,
  expansionPackAmount,
  battery10,
  calculateBatteryCost,
  setLightReachStatus,
  setLightReachIdentity,
  setLightReachTitle,
  isB = false,
}) {
  const { getOffers, offers, offer, lrAcctId, isLoading } = useOffers();
  const [modalShow, setModalShow] = useState(false);
  const [pricingText, setPricingText] = useState(+loan.isPpa);
  const [totalBatteryCost, setTotalBatteryCost] = useState(0);
  const [pricingPayload, setPricingPayload] = useState({});
  const lastLightReachRequestKeyRef = useRef("");

  /**
   * 0 no battery
   * 1-10 enphase
   * 11-20 Tesla battery
   * 21-* enphase 10c battery
   */
  const determineBattery = () => {
    if (battery10 <= 10) {
      return battery10 + expansionPack;
    } else if (battery10 > 10 && battery10 < 20) {
      return battery10 - 10 + expansionPack;
    } else {
      return battery10 - 20 + expansionPack;
    }
  };
  // TODO HIDE launch button when no provider selected
  //

  useEffect(() => {
    setPricingPayload(
      provider?.toLowerCase()?.includes("lightreach")
        ? {
          provider: "LightReach",
          offers: offers ? offers : [],
          name: offer?.name,
        }
        : provider?.toLowerCase()?.includes("goodleap")
          ? {
            provider: "GoodLeap PPA",
            offerId: undefined,
            systemSize: systemSize,
            minKwRate: 0.1,
            estimatedAnnualProduction: estimatedAnnualProduction,
            batteryCost:
              totalBatteryCost +
              (Number(expansionPackAmount) || 0) * (Number(expansionPack) || 0),
            sfProjectId: sfProjectId,
          }
          : {});
  }, [offer, expansionPack, expansionPackAmount, totalBatteryCost]);

  useEffect(() => setPricingText(+loan.isPpa), [, loan]);

  useEffect(() => {
    if (!isB) setLightReachAcctId(lrAcctId);
  }, [lrAcctId]);

  useEffect(
    () => setPricingText(offer?.id === loan.offerId),
    [, offer, isLoading]
  );

  useEffect(() => {
    if (acCoupled && +loan.isPpa === 1 && +battery10 >= 11 && +battery10 < 20) {
      setTotalBatteryCost(calculateBatteryCost());
    } else {
      setTotalBatteryCost(batteryCost);
    }
  }, [, acCoupled, acCoupledAmount, batteryCost]);

  useEffect(() => {
    if (optionType !== "TPO") return;

    if (provider?.toLowerCase()?.includes("lightreach")) {
      // Always call without acctId; API looks up or creates the account and returns acctId
      const batteryCount = determineBattery();
      const expansionPackCost =
        (Number(expansionPackAmount) || 0) * (Number(expansionPack) || 0);
      const baseBatteryCost =
        acCoupled && +loan.isPpa === 1 && +battery10 >= 11 && +battery10 < 20
          ? calculateBatteryCost()
          : (batteryCost ?? 0);
      const batteryCostForPayload =
        (Number(baseBatteryCost) || 0) + expansionPackCost;

      // Request key excludes acctId so we don't fire twice when one run has acctId and one doesn't
      const requestKey = [
        payload?.sfProjectId,
        batteryCostForPayload,
        batteryCount,
        mspForLightReachGetQuote,
        payload?.hasBattery,
      ].join("|");
      if (lastLightReachRequestKeyRef.current === requestKey) return;
      lastLightReachRequestKeyRef.current = requestKey;

      const offersPayload = {
        sfProjectId: payload?.sfProjectId,
        hasBattery: payload?.hasBattery,
        msp: mspForLightReachGetQuote,
        batteryCost: batteryCostForPayload,
        batteryCount: batteryCount,
      };
      if (batteryCount === 0 || batteryCostForPayload > 0) {
        getOffers(
          offersPayload,
          getLightReachTpoOffers,
          setLightReachAcctId,
          true,
          (resp) => {
            if (resp?.status != null && setLightReachStatus) {
              setLightReachStatus(resp.status);
            }
            if (resp?.identity != null && setLightReachIdentity) {
              setLightReachIdentity(resp.identity);
            }
            if (resp?.title != null && setLightReachTitle) {
              setLightReachTitle(resp.title);
            }
          }
        );
      }
    } else if (provider?.toLowerCase()?.includes("goodleap")) {
      // Clear so switching back to LightReach will trigger a fresh call
      lastLightReachRequestKeyRef.current = "";
      const expansionPackCost =
        (Number(expansionPackAmount) || 0) * (Number(expansionPack) || 0);
      getOffers(
        {
          state: payload?.state,
          utility: payload?.utility,
          hasBattery: payload?.hasBattery,
          sfProjectId: payload?.sfProjectId,
          batteryCost: totalBatteryCost + expansionPackCost,
        },
        getGoodLeapTpoOffers
      );
    }
  }, [
    provider,
    optionType,
    totalBatteryCost,
    expansionPack,
    expansionPackAmount,
    acCoupled,
    battery10,
    batteryCost,
    payload?.sfProjectId,
    payload?.hasBattery,
    payload?.state,
    payload?.utility,
    mspForLightReachGetQuote,
  ]);

  useEffect(() => {
    console.log("provider", provider);
  }, [provider]);

  const isTPOProvider =
    provider?.toLowerCase()?.includes("lightreach") ||
    provider?.toLowerCase()?.includes("goodleap");

  return provider ? (
    <>
      <div className="savingsText modals fin pdb0">
        {optionType === "TPO" ? "Offers" : "Products"}
        <button
          className="btn btn-link p-1"
          title="Change Product"
          style={{ color: "rgb(65,126,238)", fontSize: 22 }}
          onClick={() => setModalShow(true)}
        >
          <i
            className="bi bi-pencil-square"
            onClick={() => setModalShow(true)}
          ></i>
        </button>
      </div>
      {optionType === "TPO" && isTPOProvider && isLoading && (
        <CircularProgress size={50} color="inherit" />
      )}
      {optionType === "TPO" && !isLoading && <Offers loan={loan} />}

      <button
        className="card shadow-sm"
        style={{ width: "22rem", borderRadius: "16px" }}
        onClick={() => setModalShow(true)}
      >
        <div className="card-body pricing d-flex align-items-center">
          <div className="product-icon d-flex align-items-center justify-content-center">
            <i className="bi bi-currency-dollar"></i>
          </div>
          <div className="flex-grow-1">
            <div className="product-title">
              {optionType === "Cash"
                ? `$${customerData
                  .calculateLoanAmount()
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                : optionType === "TPO" && pricingText
                  ? `$${loan.ppaRate.toFixed(3)} kW/h $${loan.ppwRate} PPW`
                  : optionType === "TPO"
                    ? "Select Pricing"
                    : `${+loan.loanTerm} YR ${+loan.loanInterest}% APR`}
            </div>
            {/* {optionType === "Loan" && (
              <div className="product-ppw">PPW: ${ppw}</div>
            )} */}
          </div>
        </div>
      </button>
      {optionType === "TPO" && provider !== "Dividen" && (
        <TPOProductsModal
          setNeedToResyncTpoPricing={setNeedToResyncTpoPricing}
          show={modalShow}
          onHide={() => setModalShow(false)}
          setLoanAId={setLoanAId}
          curLoan={loan}
          setPpw={setPpw}
          pricingPayload={pricingPayload}
        />
      )}
      {optionType === "Loan" && (
        <LoanProductsModal
          show={modalShow}
          onHide={() => setModalShow(false)}
          mappedPrices={mappedPrices}
          loanList={loanList}
          setPpw={setPpw}
          customerData={customerData}
          provider={provider}
          setLoanAId={setLoanAId}
          curLoan={loan}
          adderTotal={adderTotal}
          ppw={ppw}
          dealerFee={dealerFee}
          fetchSungagePayments={fetchSungagePayments}
          setDealerFee={setDealerFee}
          isB={isB}
        />
      )}
    </>
  ) : (
    <div className="savingsText modals fin pdb0">Select Provider</div>
  );
}

export default ProductsPopup;
